import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Button, makeStyles, Theme } from '@material-ui/core'
import clsx from 'clsx'
import React from 'react'
import useBetaWorkspaceToggle from './hooks/useBetaWorkspaceToggle'

export interface CommentButtonsStylesProps {
    isBetaWorkspaceToggleEnabled?: boolean
}

const useStyles = makeStyles((theme: Theme) => ({
    cancelButton: (props: CommentButtonsStylesProps) => ({
        borderRadius: pxToRem(100),
        fontSize: pxToRem(12),
        backgroundColor: theme.palette.common.white,
        color: props.isBetaWorkspaceToggleEnabled ? '#475E8A;' : 'inherit',
        border: `1px solid ${
            props.isBetaWorkspaceToggleEnabled ? '#475E8A' : '#C9C9C9'
        }`,
        padding: 0,
        '&:hover': {
            backgroundColor: theme.palette.common.white,
            border: '1px solid #C9C9C9',
        },
    }),
    replyButton: (props: CommentButtonsStylesProps) => ({
        borderRadius: pxToRem(100),
        fontSize: pxToRem(12),
        fontWeight: props.isBetaWorkspaceToggleEnabled ? 500 : 'inherit',
        alignItems: props.isBetaWorkspaceToggleEnabled ? 'center' : 'inherit',
        backgroundColor: props.isBetaWorkspaceToggleEnabled
            ? '#475E8A'
            : '#A78966',
        color: theme.palette.common.white,
        border: props.isBetaWorkspaceToggleEnabled
            ? 'none'
            : '1px solid #A78966',
        padding: 0,
        '&:hover': {
            backgroundColor: props.isBetaWorkspaceToggleEnabled
                ? '#04325B'
                : '#A78966',
            color: theme.palette.common.white,
            border: props.isBetaWorkspaceToggleEnabled
                ? 'none'
                : '1px solid #A78966',
        },
    }),
    deleteButton: {
        borderRadius: pxToRem(100),
        fontSize: pxToRem(12),
        fontWeight: 500,
        alignItems: 'center',
        backgroundColor: '#B01111',
        color: theme.palette.common.white,
        padding: 0,
        '&:hover': {
            backgroundColor: '#8C0E0E',
            color: theme.palette.common.white,
        },
    },
    disabledReplyButton: (props: CommentButtonsStylesProps) => ({
        border: '1px solid #C9C9C9',
        color: props.isBetaWorkspaceToggleEnabled ? '#595959' : 'inherit',
        backgroundColor: props.isBetaWorkspaceToggleEnabled
            ? '#D0D0D0'
            : 'inherit',
    }),
    buttonContainer: {
        paddingTop: theme.spacing(1.25),
        position: 'relative',
        zIndex: 3,
        // Ensure buttons stay above comment text but within card context
        backgroundColor: 'transparent',
    },
}))

export interface CommentButtonsProps {
    leftButtonText: string
    rightButtonText: string
    leftButtonActionCallback: () => void
    rightButtonActionCallback: () => void
    areButtonsDisabled: boolean
}

export const CommentButtons = ({
    leftButtonText,
    rightButtonText,
    leftButtonActionCallback,
    rightButtonActionCallback,
    areButtonsDisabled,
}: CommentButtonsProps) => {
    const isBetaWorkspaceToggleEnabled = useBetaWorkspaceToggle()
    const classes = useStyles({ isBetaWorkspaceToggleEnabled })

    const cancelButton = (
        <GridItem>
            <Button
                variant="contained"
                disableRipple
                className={classes.cancelButton}
                onClick={rightButtonActionCallback}
                data-testid="cancelButton"
            >
                {rightButtonText}
            </Button>
        </GridItem>
    )

    const replyButton = (
        <GridItem>
            <Button
                variant="contained"
                disableRipple
                className={clsx(
                    isBetaWorkspaceToggleEnabled && leftButtonText === 'Delete'
                        ? classes.deleteButton
                        : classes.replyButton,
                    areButtonsDisabled && classes.disabledReplyButton
                )}
                onClick={leftButtonActionCallback}
                disabled={areButtonsDisabled}
                data-testid="replyButton"
            >
                {leftButtonText}
            </Button>
        </GridItem>
    )

    return (
        <GridItem data-testid="commentButtons">
            <GridContainer
                className={classes.buttonContainer}
                justifyContent={
                    isBetaWorkspaceToggleEnabled ? 'flex-end' : 'center'
                }
                spacing={1}
            >
                {isBetaWorkspaceToggleEnabled ? (
                    <>
                        {cancelButton}
                        {replyButton}
                    </>
                ) : (
                    <>
                        {replyButton}
                        {cancelButton}
                    </>
                )}
            </GridContainer>
        </GridItem>
    )
}
